import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  signal,
} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import type {ComparableMatch, ComparablesResponse} from '../../../models/valuation-comparable.models';
import {COMPARABLE_CATEGORIES, ComparableCategory} from '../../../models/valuation-comparable.models';
import {StackComponent} from '../../../shared/components/stack/stack.component';
import {ComparableComponent} from '../comparable/comparable.component';
import {ValuationTabComponent} from '../valuation-tab.component';


@Component({
  selector: 'app-comparables-tab',
  imports: [StackComponent, ComparableComponent],
  templateUrl: './comparables-tab.component.html',
  styleUrl: './comparables-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ComparablesTabComponent extends ValuationTabComponent<ComparablesResponse> {
  protected readonly tabTitle = 'Comparables';

  protected readonly categories = COMPARABLE_CATEGORIES;
  protected readonly selectedCategory = signal<ComparableCategory>('cap_rate_comparables');

  /** Included/total across all three categories, not just the one currently shown. */
  protected readonly includedCount = computed(() => this.countAcrossCategories((c) => c.included));
  protected readonly totalCount = computed(() => this.countAcrossCategories(() => true));

  constructor() {
    super()

    effect(() => {
      this.valid.set(this.includedCount() > 0);
    })
  }

  protected override loadData(): Observable<ComparablesResponse> {
    return this.valuationService.getComparables(this.sessionId())
  }

  protected setCategory(category: string): void {
    this.selectedCategory.set(category as ComparableCategory);
  }

  private countAcrossCategories(predicate: (comp: ComparableMatch) => boolean): number {
    const current = this.data();
    if (!current) return 0;
    const seenApns = new Set<string>();
    let count = 0;
    for (const cat of this.categories) {
      for (const comp of current[cat.key]) {
        if (seenApns.has(comp.apn)) continue;
        seenApns.add(comp.apn);
        if (predicate(comp)) count++;
      }
    }
    return count;
  }

  private applyIncludedEverywhere(
    data: ComparablesResponse,
    apn: string,
    included: boolean,
  ): ComparablesResponse {
    const updated = { ...data };
    for (const cat of this.categories) {
      updated[cat.key] = data[cat.key].map((c) => (c.apn === apn ? { ...c, included } : c));
    }
    return updated;
  }

  protected async toggleInclusion(comp: ComparableMatch, included: boolean): Promise<void> {
    const previous = this.data();
    if (!previous) return;

    // Optimistic local update for instant feedback -- doesn't touch
    // sessionPage.progress (nothing server-confirmed yet).
    this.data.set(this.applyIncludedEverywhere(previous, comp.apn, included));

    try {
      const response = await firstValueFrom(
        this.valuationService.updateComparableSelection(this.sessionId(), {
          comparable_id: comp.apn,
          included,
        }),
      );
      // Replace the optimistic guess with the server's authoritative result
      // (self-healing any mismatch) and sync progress from the same response.
      this.setData(response);
    } catch {
      this.data.set(previous);
      this.sessionPage.loadError.set('Failed to update comparable selection.');
    }
  }

  protected async recalculate(): Promise<void> {
    const data = await this.load(this.valuationService.refreshComparables(this.sessionId()))
    if (data) this.setData(data);
  }
}
